 "use client";

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ITeam, ITeamType, IUserFull } from '@gettactic/api';
import { useTeams, TeamAPI } from './useTeams';
import { useToggle } from './useToggle';

export interface UserTeamsAPI extends TeamAPI {
  client: {
    teams: {
      all: (params: { type: ITeamType; query?: string; hidden?: boolean }) => Promise<any>;
      forUser: (params: { userId: string; type: ITeamType }) => Promise<any>;
    };
  };
}

export function useUserTeamsList(userId: string | null, type: ITeamType, api?: UserTeamsAPI) {
  const {
    data: userTeamList,
    error,
    isLoading
  } = useQuery({
    queryKey: ['teams.forUser', type, userId],

    queryFn: async () => {
      if (!userId) throw new Error('UserId is required');
      if (!api) {
        throw new Error('API instance must be provided to useUserTeamsList hook');
      }
      const res = await api.client.teams.forUser({ userId, type });
      return res.result;
    },

    enabled: !!userId && !!api
  });
  return {
    userTeamList,
    error,
    isLoading
  };
}

export function useUserTeams(user: IUserFull, initialList: ITeam[], api?: TeamAPI) {
  const [teamMemberList, setTeamMemberList] = useState(initialList);

  const { teamList } = useTeams({ type: 'directory', hidden: false, api: api as TeamAPI });

  const [displayOnlyAssigned, toggleDisplayOnlyAssigned] = useToggle(
    initialList.length > 0
  );
  const [teamsQuery, setTeamsQuery] = useState('');

  function isMember(team: ITeam) {
    return teamMemberList.find((x) => x.id === team.id);
  }
  function addMember(team: ITeam) {
    if (isMember(team)) {
      return;
    }
    setTeamMemberList((x) => x.concat([team]));
  }
  function removeMember(team: ITeam) {
    setTeamMemberList((x) => x.filter((x) => x.id !== team.id));
  }

  return {
    displayOnlyAssigned,
    toggleDisplayOnlyAssigned,
    teamsQuery,
    setTeamsQuery,
    teamList,
    teamMemberList,
    isMember,
    addMember,
    removeMember
  };
} 